import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Button, CircularProgress, IconButton, InputAdornment, TextField, Tooltip } from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import AddRoundedIcon from "@material-ui/icons/AddRounded";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import MenuBookRoundedIcon from "@material-ui/icons/MenuBookRounded";
import SaveRoundedIcon from "@material-ui/icons/SaveRounded";
import { toast } from "react-toastify";
import { format, parseISO } from "date-fns";

import MainContainer from "../../components/MainContainer";
import ConfirmationModal from "../../components/ConfirmationModal";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import openSocket from "../../services/socket-io";
import toastError from "../../errors/toastError";
import { bannerNavy, bannerTitulo, bannerSub, botaoVerdeBrilho, campoNavy, VERDE } from "../../utils/estiloNavy";
import { sanitizarHtml, textoPuro } from "../../utils/regrasHtml";
import { BlocoEditor, BlocoVisual, TIPOS, novoBloco } from "./Blocos";
import { ICONES_SECAO, IconeSecao } from "./icones";

const CORES_SECAO = ["#1E9855", "#2563eb", "#9333ea", "#ea580c", "#dc2626", "#0891b2", "#db2777", "#ca8a04"];
const ICONES_SUGERIDOS = Object.keys(ICONES_SECAO);

const useStyles = makeStyles((theme) => {
  const escuro = theme.palette.type === "dark";
  const borda = escuro ? "rgba(255,255,255,.09)" : "#e2e8f0";
  const cartao = escuro ? "rgba(15,23,42,.72)" : "#fff";
  return {
    // Letras brilhantes / grandes usadas no texto (editor e leitura)
    "@global": {
      "@keyframes rgDeslizar": { "0%": { backgroundPosition: "0% 50%" }, "100%": { backgroundPosition: "300% 50%" } },
      "@keyframes rgPiscar": { "0%,100%": { opacity: 1 }, "50%": { opacity: 0.35 } },
      ".rg-brilho-arcoiris, .rg-brilho-verde, .rg-brilho-ouro, .rg-brilho-fogo": {
        fontWeight: 800,
        backgroundSize: "300% 100%",
        WebkitBackgroundClip: "text",
        backgroundClip: "text",
        color: "transparent !important",
        WebkitTextFillColor: "transparent",
        animation: "rgDeslizar 4s linear infinite",
      },
      ".rg-brilho-arcoiris": {
        backgroundImage: "linear-gradient(90deg,#22c55e,#06b6d4,#a855f7,#f43f5e,#22c55e)",
        filter: "drop-shadow(0 0 6px rgba(168,85,247,.35))",
      },
      ".rg-brilho-verde": {
        backgroundImage: "linear-gradient(90deg,#16a34a,#a3e635,#22c55e,#16a34a)",
        filter: "drop-shadow(0 0 6px rgba(34,197,94,.55))",
      },
      ".rg-brilho-ouro": {
        backgroundImage: "linear-gradient(90deg,#b45309,#fde047,#f59e0b,#b45309)",
        filter: "drop-shadow(0 0 6px rgba(245,158,11,.5))",
      },
      ".rg-brilho-fogo": {
        backgroundImage: "linear-gradient(90deg,#dc2626,#f97316,#facc15,#dc2626)",
        filter: "drop-shadow(0 0 6px rgba(239,68,68,.5))",
      },
      ".rg-alerta, .rg-efeito-alerta": {
        color: "#ef4444 !important",
        fontWeight: 800,
        animation: "rgPiscar 1.2s ease-in-out infinite",
        textShadow: "0 0 10px rgba(239,68,68,.45)",
      },
      ".rg-grande, .rg-tam-grande": { fontSize: "1.35em", fontWeight: 800, lineHeight: 1.3 },
      // listas e parágrafos que o editor gera
      ".rg-texto p": { margin: 0 },
    },

    pagina: { padding: theme.spacing(2.5, 2, 6) },
    topo: {
      ...bannerNavy(theme),
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: 16,
      marginBottom: theme.spacing(2),
    },
    topoIcone: {
      width: 48,
      height: 48,
      borderRadius: 14,
      display: "grid",
      placeItems: "center",
      background: "linear-gradient(135deg,#22c55e,#15803d)",
      boxShadow: "0 6px 18px rgba(34,197,94,.35)",
      marginRight: 14,
      flexShrink: 0,
    },
    topoTitulo: bannerTitulo,
    topoSub: bannerSub,
    busca: {
      ...campoNavy(theme),
      width: 260,
      "& .MuiOutlinedInput-root": {
        ...campoNavy(theme)["& .MuiOutlinedInput-root"],
        backgroundColor: "rgba(255,255,255,0.08) !important",
        color: "#fff",
        "& fieldset": { borderColor: "rgba(255,255,255,0.2) !important" },
      },
      "& input::placeholder": { color: "#cbd5e1", opacity: 1 },
    },
    botaoNovo: botaoVerdeBrilho,

    layout: {
      display: "grid",
      gridTemplateColumns: "240px 1fr",
      gap: 18,
      alignItems: "start",
      [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr" },
    },
    indice: {
      position: "sticky",
      top: 12,
      borderRadius: 16,
      padding: 10,
      background: cartao,
      border: `1px solid ${borda}`,
      [theme.breakpoints.down("sm")]: { position: "static" },
    },
    indiceTitulo: {
      fontSize: "0.68rem",
      fontWeight: 800,
      letterSpacing: ".08em",
      textTransform: "uppercase",
      color: theme.palette.text.secondary,
      padding: "4px 8px 8px",
    },
    indiceItem: {
      width: "100%",
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "8px 10px",
      borderRadius: 10,
      border: "none",
      background: "transparent",
      color: theme.palette.text.primary,
      fontFamily: "inherit",
      fontSize: "0.86rem",
      fontWeight: 600,
      textAlign: "left",
      cursor: "pointer",
      "&:hover": { background: escuro ? "rgba(255,255,255,.06)" : "#f1f5f9" },
    },
    indiceCor: { width: 4, height: 18, borderRadius: 4, flexShrink: 0 },

    secoes: { display: "flex", flexDirection: "column", gap: 18 },
    secao: {
      borderRadius: 20,
      background: cartao,
      border: `1px solid ${borda}`,
      boxShadow: escuro ? "0 10px 30px rgba(0,0,0,.3)" : "0 10px 30px rgba(15,23,42,.06)",
      overflow: "hidden",
      animation: "iaSubir .35s ease backwards",
      scrollMarginTop: 12,
    },
    secaoTopo: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      padding: "16px 18px",
      borderBottom: `1px solid ${borda}`,
      background: "linear-gradient(90deg, var(--rg-fundo), transparent 70%)",
    },
    secaoIcone: {
      width: 42,
      height: 42,
      borderRadius: 12,
      display: "grid",
      placeItems: "center",
      color: "var(--rg-cor)",
      background: "var(--rg-fundo)",
      border: "1px solid var(--rg-cor)",
      flexShrink: 0,
      "& svg": { fontSize: 24 },
    },
    secaoTitulo: { fontSize: "1.15rem", fontWeight: 800, color: theme.palette.text.primary, lineHeight: 1.2 },
    secaoInfo: { fontSize: "0.72rem", color: theme.palette.text.secondary, marginTop: 2 },
    secaoAcoes: { marginLeft: "auto", display: "flex", gap: 2, flexShrink: 0 },
    secaoCorpo: { padding: 18, display: "flex", flexDirection: "column", gap: 14 },

    // edição
    editorTopo: {
      display: "flex",
      flexDirection: "column",
      gap: 10,
      padding: 16,
      borderBottom: `1px solid ${borda}`,
      background: escuro ? "rgba(30,152,85,.08)" : "#f0fdf4",
    },
    linha: { display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" },
    campo: { ...campoNavy(theme) },
    emoji: {
      width: 34,
      height: 34,
      borderRadius: 10,
      border: `1px solid ${borda}`,
      background: escuro ? "rgba(255,255,255,.03)" : "#fff",
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      color: theme.palette.text.secondary,
      "&:hover": { color: VERDE, borderColor: VERDE },
    },
    emojiAtivo: {
      borderColor: VERDE,
      color: "#fff !important",
      background: `linear-gradient(135deg, #22c55e, ${VERDE}) !important`,
      boxShadow: "0 0 0 3px rgba(30,152,85,.18)",
    },
    cor: { width: 26, height: 26, borderRadius: "50%", border: "3px solid transparent", cursor: "pointer", padding: 0 },
    blocoEdicao: {
      border: `1px dashed ${borda}`,
      borderRadius: 14,
      padding: 12,
      background: escuro ? "rgba(255,255,255,.015)" : "#fcfdfe",
    },
    blocoBarra: { display: "flex", alignItems: "center", gap: 4, marginBottom: 8 },
    blocoTipo: {
      fontSize: "0.7rem",
      fontWeight: 800,
      letterSpacing: ".06em",
      textTransform: "uppercase",
      color: theme.palette.text.secondary,
      marginRight: "auto",
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
    },
    adicionar: {
      border: `2px dashed ${borda}`,
      borderRadius: 14,
      padding: 12,
      display: "flex",
      flexWrap: "wrap",
      gap: 8,
      alignItems: "center",
    },
    adicionarRotulo: { fontSize: "0.8rem", fontWeight: 800, color: theme.palette.text.secondary, marginRight: 4 },
    tipoBotao: {
      border: `1px solid ${borda}`,
      borderRadius: 10,
      padding: "6px 10px",
      background: escuro ? "rgba(255,255,255,.03)" : "#fff",
      color: theme.palette.text.primary,
      fontFamily: "inherit",
      fontWeight: 700,
      fontSize: "0.8rem",
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      "& svg": { color: VERDE },
      "&:hover": { borderColor: VERDE, color: VERDE },
    },
    rodape: {
      display: "flex",
      justifyContent: "flex-end",
      gap: 8,
      padding: "12px 18px",
      borderTop: `1px solid ${borda}`,
      position: "sticky",
      bottom: 0,
      background: cartao,
      backdropFilter: "blur(8px)",
    },
    vazio: {
      textAlign: "center",
      padding: "60px 20px",
      borderRadius: 20,
      border: `2px dashed ${borda}`,
      color: theme.palette.text.secondary,
    },
  };
});

const limparBlocos = (blocos) =>
  blocos.map((b) => {
    const c = { ...b };
    if (c.html !== undefined) c.html = sanitizarHtml(c.html);
    ["itens", "pode", "naoPode"].forEach((k) => {
      if (Array.isArray(c[k])) c[k] = c[k].map((t) => String(t).trim()).filter(Boolean);
    });
    return c;
  });

const textoDaSecao = (s) =>
  [
    s.titulo,
    ...(s.blocos || []).map((b) =>
      [b.texto, b.titulo, b.legenda, textoPuro(b.html), ...(b.itens || []), ...(b.pode || []), ...(b.naoPode || [])]
        .filter(Boolean)
        .join(" ")
    ),
  ]
    .join(" ")
    .toLowerCase();

const Regras = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const ehAdmin = user?.profile === "admin";

  const [secoes, setSecoes] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [busca, setBusca] = useState("");
  const [editando, setEditando] = useState(null); // rascunho da seção em edição
  const [salvando, setSalvando] = useState(false);
  const [apagar, setApagar] = useState(null);

  const carregar = useCallback(async () => {
    try {
      const { data } = await api.get("/regras");
      setSecoes(Array.isArray(data) ? data : []);
    } catch (err) {
      toastError(err);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    carregar();
    const socket = openSocket();
    const aoMudar = () => carregar();
    socket.on("regras", aoMudar);
    return () => socket.off("regras", aoMudar);
  }, [carregar]);

  const filtradas = useMemo(() => {
    const q = busca.trim().toLowerCase();
    if (!q) return secoes;
    return secoes.filter((s) => textoDaSecao(s).includes(q));
  }, [secoes, busca]);

  const irPara = (id) => {
    const el = document.getElementById(`regra-${id}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // ── ações do admin ──
  const novaSecao = async () => {
    if (editando) return toast.info("Salve ou cancele a seção que está editando.");
    try {
      const { data } = await api.post("/regras", {
        titulo: "Nova seção",
        icone: "pin",
        cor: VERDE,
        blocos: [novoBloco("texto")],
      });
      setSecoes((l) => [...l, data]);
      setEditando({ ...data });
      setTimeout(() => irPara(data.id), 100);
    } catch (err) {
      toastError(err);
    }
  };

  const salvar = async () => {
    if (!editando) return;
    setSalvando(true);
    try {
      const { data } = await api.put(`/regras/${editando.id}`, {
        titulo: editando.titulo,
        icone: editando.icone,
        cor: editando.cor,
        blocos: limparBlocos(editando.blocos || []),
      });
      setSecoes((l) => l.map((s) => (s.id === data.id ? data : s)));
      setEditando(null);
      toast.success("Regras salvas. Toda a equipe já está vendo.");
    } catch (err) {
      toastError(err);
    } finally {
      setSalvando(false);
    }
  };

  const mover = async (indice, dir) => {
    const nova = [...secoes];
    const alvo = indice + dir;
    if (alvo < 0 || alvo >= nova.length) return;
    [nova[indice], nova[alvo]] = [nova[alvo], nova[indice]];
    setSecoes(nova);
    try {
      await api.put("/regras/ordem", { ids: nova.map((s) => s.id) });
    } catch (err) {
      toastError(err);
      carregar();
    }
  };

  const confirmarApagar = async () => {
    try {
      await api.delete(`/regras/${apagar.id}`);
      setSecoes((l) => l.filter((s) => s.id !== apagar.id));
      if (editando?.id === apagar.id) setEditando(null);
    } catch (err) {
      toastError(err);
    }
    setApagar(null);
  };

  // ── edição de blocos ──
  const mudarBloco = (i, parcial) =>
    setEditando((e) => ({ ...e, blocos: e.blocos.map((b, j) => (j === i ? { ...b, ...parcial } : b)) }));
  const moverBloco = (i, dir) =>
    setEditando((e) => {
      const b = [...e.blocos];
      const alvo = i + dir;
      if (alvo < 0 || alvo >= b.length) return e;
      [b[i], b[alvo]] = [b[alvo], b[i]];
      return { ...e, blocos: b };
    });
  const duplicarBloco = (i) =>
    setEditando((e) => {
      const b = [...e.blocos];
      b.splice(i + 1, 0, { ...b[i], id: Math.random().toString(36).slice(2, 10) });
      return { ...e, blocos: b };
    });
  const removerBloco = (i) => setEditando((e) => ({ ...e, blocos: e.blocos.filter((_, j) => j !== i) }));
  const adicionarBloco = (tipo) => setEditando((e) => ({ ...e, blocos: [...(e.blocos || []), novoBloco(tipo)] }));

  const varsCor = (cor) => ({ "--rg-cor": cor || VERDE, "--rg-fundo": `${cor || VERDE}22` });

  const renderEdicao = (s) => (
    <div key={s.id} id={`regra-${s.id}`} className={classes.secao} style={varsCor(editando.cor)}>
      <div className={classes.editorTopo}>
        <TextField
          className={classes.campo}
          variant="outlined"
          size="small"
          label="Título da seção"
          value={editando.titulo}
          onChange={(e) => {
            // React 16 recicla o evento: lê o valor ANTES de atualizar o estado
            const titulo = e.target.value;
            setEditando((d) => ({ ...d, titulo }));
          }}
          inputProps={{ style: { fontWeight: 800 } }}
        />
        <div className={classes.linha}>
          <span style={{ fontSize: "0.78rem", fontWeight: 700, opacity: 0.75 }}>Ícone:</span>
          {ICONES_SUGERIDOS.map((ic) => (
            <button
              key={ic}
              type="button"
              className={`${classes.emoji} ${editando.icone === ic ? classes.emojiAtivo : ""}`}
              onClick={() => setEditando((d) => ({ ...d, icone: ic }))}
            >
              <IconeSecao icone={ic} fontSize="small" />
            </button>
          ))}
        </div>
        <div className={classes.linha}>
          <span style={{ fontSize: "0.78rem", fontWeight: 700, opacity: 0.75 }}>Cor:</span>
          {CORES_SECAO.map((c) => (
            <button
              key={c}
              type="button"
              className={classes.cor}
              style={{ background: c, borderColor: editando.cor === c ? "#fff" : "transparent", boxShadow: editando.cor === c ? `0 0 0 2px ${c}` : "none" }}
              onClick={() => setEditando((d) => ({ ...d, cor: c }))}
            />
          ))}
        </div>
      </div>

      <div className={classes.secaoCorpo}>
        {(editando.blocos || []).map((b, i) => {
          const tipo = TIPOS.find((t) => t.tipo === b.tipo);
          return (
            <div key={b.id} className={classes.blocoEdicao}>
              <div className={classes.blocoBarra}>
                <span className={classes.blocoTipo}>
                  {tipo?.Icone && <tipo.Icone style={{ fontSize: 15 }} />} {tipo?.rotulo}
                </span>
                <Tooltip title="Subir">
                  <span>
                    <IconButton size="small" disabled={i === 0} onClick={() => moverBloco(i, -1)}>
                      <ArrowUpwardIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Descer">
                  <span>
                    <IconButton size="small" disabled={i === editando.blocos.length - 1} onClick={() => moverBloco(i, 1)}>
                      <ArrowDownwardIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Duplicar">
                  <IconButton size="small" onClick={() => duplicarBloco(i)}>
                    <FileCopyOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Remover bloco">
                  <IconButton size="small" onClick={() => removerBloco(i)}>
                    <DeleteOutlineIcon fontSize="small" style={{ color: "#ef4444" }} />
                  </IconButton>
                </Tooltip>
              </div>
              <BlocoEditor bloco={b} mudar={(p) => mudarBloco(i, p)} />
            </div>
          );
        })}

        <div className={classes.adicionar}>
          <span className={classes.adicionarRotulo}>+ Adicionar:</span>
          {TIPOS.map((t) => (
            <button key={t.tipo} type="button" className={classes.tipoBotao} onClick={() => adicionarBloco(t.tipo)}>
              <t.Icone style={{ fontSize: 17 }} /> {t.rotulo}
            </button>
          ))}
        </div>
      </div>

      <div className={classes.rodape}>
        <Button onClick={() => setEditando(null)} disabled={salvando} style={{ textTransform: "none", fontWeight: 700 }}>
          Cancelar
        </Button>
        <Button
          className={classes.botaoNovo}
          onClick={salvar}
          disabled={salvando}
          startIcon={salvando ? <CircularProgress size={16} style={{ color: "#fff" }} /> : <SaveRoundedIcon />}
        >
          Salvar
        </Button>
      </div>
    </div>
  );

  const renderLeitura = (s, indice) => (
    <div key={s.id} id={`regra-${s.id}`} className={classes.secao} style={varsCor(s.cor)}>
      <div className={classes.secaoTopo}>
        <div className={classes.secaoIcone}>
          <IconeSecao icone={s.icone} />
        </div>
        <div style={{ minWidth: 0 }}>
          <div className={classes.secaoTitulo}>{s.titulo}</div>
          {s.updatedAt && (
            <div className={classes.secaoInfo}>
              Atualizado em {format(parseISO(s.updatedAt), "dd/MM/yyyy 'às' HH:mm")}
              {s.autor?.name ? ` por ${s.autor.name}` : ""}
            </div>
          )}
        </div>
        {ehAdmin && (
          <div className={classes.secaoAcoes}>
            <Tooltip title="Subir seção">
              <span>
                <IconButton size="small" disabled={indice === 0 || !!busca} onClick={() => mover(indice, -1)}>
                  <ArrowUpwardIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Descer seção">
              <span>
                <IconButton size="small" disabled={indice === secoes.length - 1 || !!busca} onClick={() => mover(indice, 1)}>
                  <ArrowDownwardIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Editar">
              <IconButton
                size="small"
                onClick={() =>
                  editando
                    ? toast.info("Salve ou cancele a seção que está editando.")
                    : setEditando(JSON.parse(JSON.stringify(s)))
                }
              >
                <EditOutlinedIcon fontSize="small" style={{ color: VERDE }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Excluir seção">
              <IconButton size="small" onClick={() => setApagar(s)}>
                <DeleteOutlineIcon fontSize="small" style={{ color: "#ef4444" }} />
              </IconButton>
            </Tooltip>
          </div>
        )}
      </div>
      <div className={classes.secaoCorpo}>
        {(s.blocos || []).length ? (
          s.blocos.map((b) => <BlocoVisual key={b.id} bloco={b} />)
        ) : (
          <div style={{ opacity: 0.6, fontSize: "0.9rem" }}>Seção ainda sem conteúdo.</div>
        )}
      </div>
    </div>
  );

  return (
    <MainContainer>
      <ConfirmationModal
        title={apagar ? `Excluir a seção "${apagar.titulo}"?` : ""}
        open={!!apagar}
        onClose={() => setApagar(null)}
        onConfirm={confirmarApagar}
      >
        A seção some para toda a equipe. Isso não tem como desfazer.
      </ConfirmationModal>

      <div className={classes.pagina}>
        <div className={classes.topo}>
          <div style={{ display: "flex", alignItems: "center" }}>
            <div className={classes.topoIcone}>
              <MenuBookRoundedIcon />
            </div>
            <div>
              <div className={classes.topoTitulo}>Regras e Manual</div>
              <div className={classes.topoSub}>
                {ehAdmin
                  ? "Você é administrador: crie e edite as regras que toda a equipe vai ler."
                  : "Leia com atenção: estas regras valem para toda a equipe."}
              </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <TextField
              className={classes.busca}
              variant="outlined"
              size="small"
              placeholder="Buscar nas regras..."
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: "#cbd5e1" }} />
                  </InputAdornment>
                ),
              }}
            />
            {ehAdmin && (
              <Button className={classes.botaoNovo} startIcon={<AddRoundedIcon />} onClick={novaSecao}>
                Nova seção
              </Button>
            )}
          </div>
        </div>

        {carregando ? (
          <div style={{ textAlign: "center", padding: 60 }}>
            <CircularProgress style={{ color: VERDE }} />
          </div>
        ) : !secoes.length ? (
          <div className={classes.vazio}>
            <MenuBookRoundedIcon style={{ fontSize: 48, color: VERDE }} />
            <div style={{ fontWeight: 800, fontSize: "1.1rem", margin: "8px 0 4px" }}>Nenhuma regra cadastrada ainda</div>
            {ehAdmin ? (
              <Button className={classes.botaoNovo} startIcon={<AddRoundedIcon />} onClick={novaSecao} style={{ marginTop: 12 }}>
                Criar a primeira seção
              </Button>
            ) : (
              <div>Quando o administrador publicar as regras, elas aparecem aqui.</div>
            )}
          </div>
        ) : (
          <div className={classes.layout}>
            <div className={classes.indice}>
              <div className={classes.indiceTitulo}>Índice</div>
              {filtradas.map((s) => (
                <button key={s.id} type="button" className={classes.indiceItem} onClick={() => irPara(s.id)}>
                  <span className={classes.indiceCor} style={{ background: s.cor || VERDE }} />
                  <IconeSecao icone={s.icone} style={{ fontSize: 17, color: s.cor || VERDE }} />
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.titulo}</span>
                </button>
              ))}
              {!filtradas.length && (
                <div style={{ padding: 8, fontSize: "0.82rem", opacity: 0.7 }}>Nada encontrado.</div>
              )}
            </div>

            <div className={classes.secoes}>
              {filtradas.map((s) =>
                editando && editando.id === s.id
                  ? renderEdicao(s)
                  : renderLeitura(s, secoes.findIndex((x) => x.id === s.id))
              )}
            </div>
          </div>
        )}
      </div>
    </MainContainer>
  );
};

export default Regras;
